import {useCallback, useState} from 'react';
import {
  AccessDeniedError,
  ApiError,
  RateLimitedError,
  confirmOtp,
  requestOtp,
} from '~/lib/api';
import {useAuth} from '~/lib/auth';
import type {VerificationStep} from './types';

function getErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'invalid_code':
        return 'Invalid code. Please try again.';
      case 'code_expired':
        return 'Code expired. Click Resend to get a new one.';
      case 'max_attempts_exceeded':
        return 'Too many attempts. Click Resend to get a new code.';
    }

    if (err instanceof AccessDeniedError) {
      return 'You do not have access to this content.';
    }
    if (err instanceof RateLimitedError) {
      return 'Too many requests. Please try again later.';
    }
  }

  return 'Something went wrong. Please try again.';
}

function isVerificationNotRequired(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'verification_not_required';
}

export function useVerificationFlow() {
  const {identifier, handleVerified, reauthenticate} = useAuth();
  const [step, setStep] = useState<VerificationStep>('email');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback(
    (err: unknown) => {
      // The page was made public while the visitor was mid-verification. The
      // OTP endpoints refuse to continue, so bootstrap like a fresh visit.
      if (isVerificationNotRequired(err)) {
        void reauthenticate();
        return;
      }
      setError(getErrorMessage(err));
    },
    [reauthenticate],
  );

  const submitEmail = useCallback(
    async (emailValue: string) => {
      setIsLoading(true);
      setError(null);

      try {
        await requestOtp(identifier, emailValue);
        setEmail(emailValue);
        setStep('otp');
      } catch (err) {
        handleError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [identifier, handleError],
  );

  const submitOtp = useCallback(
    async (code: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await confirmOtp(identifier, email, code);
        handleVerified(
          response.data.token,
          response.data.organization_identifier,
        );
      } catch (err) {
        handleError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [identifier, email, handleVerified, handleError],
  );

  const resendCode = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await requestOtp(identifier, email);
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [identifier, email, handleError]);

  return {
    step,
    email,
    isLoading,
    error,
    submitEmail,
    submitOtp,
    resendCode,
  };
}
